import { PageHero, StatsBar, ProcessSection, WhyChoose, CtaBand } from "@/components/site/sections";
import { Reveal, Overline } from "@/components/site/ui";
import { IMAGES, TEAM } from "@/data/content";
import { Award, Layers, Code2 } from "lucide-react";

const ICONS = { award: Award, layers: Layers, code: Code2 };

const VALUES = [
  { t: "Design-first", d: "Every home starts with taste, proportion and how light moves through a space." },
  { t: "Engineered", d: "Precision manufacturing means finishes that stay flawless for decades." },
  { t: "Transparent", d: "Itemised, flat pricing. You always know exactly what you're paying for." },
  { t: "On time", d: "A dedicated project manager and a locked delivery date you can plan around." },
];

export default function About() {
  return (
    <>
      <PageHero
        overline="About Spherio Living"
        title="A technology-led interior company, built for how India lives."
        subtitle="We're designers, architects and engineers obsessed with turning houses into homes — beautifully and on time."
        image={IMAGES.interior2}
      />

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>Our story</Overline>
            <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5 leading-tight">
              From a single kitchen to thousands of homes.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-[#5C574F] text-lg leading-relaxed">
          <Reveal>
            <p>
              Spherio Living began with a simple frustration: home interiors in India were slow, opaque and
              unpredictable. We set out to fix that with technology — photoreal 3D design, our own
              precision factory and radically transparent pricing.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p>
              Today, Spherio Living designs and delivers modular kitchens, wardrobes and complete home
              interiors for thousands of families, backed by a 10-year warranty and a promise: what
              you approve on screen is exactly what you live in.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10 rounded-2xl overflow-hidden">
          {VALUES.map((v, i) => (
            <Reveal key={v.t} delay={i * 0.08} className="bg-white p-8">
              <h3 className="font-head text-xl font-semibold text-[#1C1A17]">{v.t}</h3>
              <p className="mt-3 text-sm text-[#5C574F] leading-relaxed">{v.d}</p>
            </Reveal>
          ))}
        </div>
      </div>

      <StatsBar dark />

      {/* Team */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
        <div className="max-w-2xl">
          <Reveal>
            <Overline>The Team</Overline>
            <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5 leading-tight">
              Engineers at the Helm.
            </h2>
            <p className="mt-5 text-[#5C574F] text-lg">
              Meet the minds behind Spherio Living — combining structural expertise with cutting-edge
              data analysis to redefine home improvement.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="team-grid">
          {TEAM.map((m, i) => {
            const Icon = ICONS[m.icon] || Award;
            return (
              <Reveal key={m.name} delay={i * 0.08}>
                <article
                  data-testid={`team-${m.icon}`}
                  className="group h-full rounded-2xl bg-white border border-black/10 p-8 hover:border-[#1C1A17]/40 transition-colors duration-500"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#1C1A17]/12 text-[#1C1A17]">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 font-head text-2xl font-medium tracking-tight">{m.name}</h3>
                  <div className="mt-2 text-[11px] tracking-[0.28em] uppercase font-bold text-[#1C1A17]">
                    {m.role}
                  </div>
                  <p className="mt-5 text-[#5C574F] leading-relaxed">{m.bio}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      <WhyChoose />
      <ProcessSection />
      <CtaBand />
    </>
  );
}
